import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Send, Loader2, MessageSquare, Mail as MailIcon, Copy, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminQuickSend() {
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // form state
  const [clientId, setClientId] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [smsTo, setSmsTo] = useState('');
  const [itemType, setItemType] = useState('program');
  const [itemId, setItemId] = useState('');
  const [channel, setChannel] = useState('email');
  const [customNote, setCustomNote] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, p, e] = await Promise.all([
          axios.get(`${API_URL}/api/admin/clients`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
          axios.get(`${API_URL}/api/events`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setClients(Array.isArray(c.data) ? c.data : (c.data?.clients || []));
        setPrograms((p.data || []).filter(isPriced));
        setEvents((e.data || []).filter(isPriced));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onPickClient = (id) => {
    setClientId(id);
    const c = clients.find((x) => x.client_id === id || x.user_id === id);
    if (c) {
      setClientEmail(c.email || '');
      setClientName(c.name || '');
      if (c.phone) setSmsTo(c.phone);
    }
  };

  const items = itemType === 'program' ? programs : events;

  const send = async (e) => {
    e.preventDefault();
    if (!itemId) { toast.error('Pick a program or event'); return; }
    setSending(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/quick-send/pay-link`, {
        client_email: clientEmail,
        client_name: clientName,
        sms_to: smsTo,
        item_type: itemType,
        item_id: itemId,
        channel,
        custom_note: customNote,
      }, { withCredentials: true });
      setLastResult(res.data);
      toast.success('Pay link sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('Copied'); } catch { toast.error('Copy failed'); }
  };

  if (loading) {
    return <AdminLayout title="Quick Send"><div className="text-center py-12 text-[#94A3B8]"><Loader2 className="inline animate-spin mr-2" /> Loading…</div></AdminLayout>;
  }

  return (
    <AdminLayout title="Quick Send Pay Link">
      <div className="max-w-3xl space-y-6">
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1D4ED8] text-white rounded-xl p-6">
          <h1 className="text-2xl font-bold mb-1" data-testid="quick-send-title">Quick Send Pay Link</h1>
          <p className="text-white/80 text-sm">Send a branded payment link via email or SMS in a single click. Stripe Payment Link is auto-generated if one does not exist for the program.</p>
        </div>

        <form onSubmit={send} className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-5">
          {/* Pick client */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">Client</label>
            <select
              value={clientId}
              onChange={(e) => onPickClient(e.target.value)}
              className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm text-[#0A0F1C]"
              data-testid="qs-client"
            >
              <option value="">-- Type the email/name below OR pick an existing client --</option>
              {clients.map((c) => (
                <option key={c.client_id || c.user_id || c.email} value={c.client_id || c.user_id}>
                  {c.name} ({c.email})
                </option>
              ))}
            </select>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Client email" v={clientEmail} onChange={setClientEmail} placeholder="client@example.com" testid="qs-email" />
            <TextField label="Client name" v={clientName} onChange={setClientName} placeholder="First Last" testid="qs-name" />
          </div>
          <TextField label="SMS phone (E.164 e.g. +19125551234)" v={smsTo} onChange={setSmsTo} placeholder="+19125551234" testid="qs-sms" />

          {/* Item picker */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">Item type</label>
              <select value={itemType} onChange={(e) => { setItemType(e.target.value); setItemId(''); }} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="qs-item-type">
                <option value="program">Program</option>
                <option value="event">Event</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">{itemType === 'program' ? 'Program' : 'Event'}</label>
              <select value={itemId} onChange={(e) => setItemId(e.target.value)} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="qs-item-id" required>
                <option value="">-- Select --</option>
                {items.map((it) => (
                  <option key={it.program_id || it.event_id} value={it.program_id || it.event_id}>
                    {it.title || it.name} ({typeof it.price === 'number' ? `$${it.price.toLocaleString()}` : it.price})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Channel */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">Channel</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'email', label: 'Email', icon: MailIcon },
                { v: 'sms',   label: 'SMS',   icon: MessageSquare },
                { v: 'both',  label: 'Both',  icon: Send },
              ].map((opt) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setChannel(opt.v)}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border text-sm font-bold transition-colors ${channel === opt.v ? 'border-[#1D4ED8] bg-[#EFF6FF] text-[#1D4ED8]' : 'border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]'}`}
                    data-testid={`qs-channel-${opt.v}`}
                  >
                    <Icon size={14} /> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">Personal note (optional)</label>
            <textarea
              value={customNote}
              onChange={(e) => setCustomNote(e.target.value)}
              rows={3}
              maxLength={600}
              placeholder="Hey - great chatting with you. Here's the link to lock in your spot."
              className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
              data-testid="qs-note"
            />
          </div>

          <button
            type="submit"
            disabled={sending || !itemId}
            className="btn-primary-orange inline-flex items-center justify-center gap-2 w-full sm:w-auto"
            data-testid="qs-send"
          >
            {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Pay Link <Send size={16} /></>}
          </button>
        </form>

        {lastResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="qs-result">
            <h3 className="font-bold text-[#0A0F1C] mb-2">Sent!</h3>
            <div className="text-sm space-y-1 text-[#0A0F1C]">
              <div><strong>Item:</strong> {lastResult.item_name} (${(lastResult.amount || 0).toLocaleString()})</div>
              {lastResult.email && <div><strong>Email:</strong> {lastResult.email}</div>}
              {lastResult.sms && <div><strong>SMS:</strong> {lastResult.sms}</div>}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <code className="bg-white border border-[#E2E8F0] px-2 py-1 rounded text-xs font-mono text-[#475569] truncate max-w-md">{lastResult.pay_url}</code>
                <button onClick={() => copy(lastResult.pay_url)} className="btn-outline text-xs inline-flex items-center gap-1">
                  <Copy size={12} /> Copy
                </button>
                <a href={lastResult.pay_url} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs inline-flex items-center gap-1">
                  <ExternalLink size={12} /> Open
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function TextField({ label, v, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 inline-block">{label}</span>
      <input
        type="text"
        value={v || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm text-[#0A0F1C]"
        data-testid={testid}
      />
    </label>
  );
}

function isPriced(item) {
  const raw = String(item.price ?? '').replace('$', '').replace(',', '').replace('Free', '0').trim();
  return raw && raw !== '0';
}
